import React, { useState } from 'react';
import {
  Button,
  Field,
  Input,
  Text,
  Textarea,
} from '@fluentui/react-components';
import { CUSTOM_DISCLAIMER_FIELD_LIMITS } from '../../constants/customDisclaimers';
import type { DisclaimerDraft, DisclaimerEditorFormProps } from '../../types/customDisclaimers';
import { DisclaimerPolicyPicker } from './DisclaimerPolicyPicker';
import { DisclaimerPreviewDialog } from './DisclaimerPreviewDialog';
import { useStyles } from './DisclaimerEditorForm.style';

export const DisclaimerEditorForm: React.FC<DisclaimerEditorFormProps> = ({
  draft,
  onChange,
  errors = {},
  currentDisclaimerId,
  disabled = false,
}) => {
  const styles = useStyles();
  const [previewOpen, setPreviewOpen] = useState(false);
  const canGeneratePreview = Boolean(draft.name.trim() && draft.organisationName.trim());

  const updateDraft = (changes: Partial<DisclaimerDraft>) => onChange({ ...draft, ...changes });

  return (
    <div className={styles.form} data-testid="disclaimer-editor-form">
      <div className={styles.field}>
        <Field
          label="Disclaimer name"
          required
          validationState={errors.name ? 'error' : 'none'}
          validationMessage={errors.name}
        >
          <Input
            value={draft.name}
            onChange={(_event, data) => updateDraft({ name: data.value })}
            maxLength={CUSTOM_DISCLAIMER_FIELD_LIMITS.name}
            placeholder="Enter disclaimer name..."
            disabled={disabled}
            data-testid="disclaimer-name-input"
          />
        </Field>
        <Text className={styles.counter} data-testid="disclaimer-name-counter">
          {draft.name.length}/{CUSTOM_DISCLAIMER_FIELD_LIMITS.name}
        </Text>
      </div>

      <div className={styles.field}>
        <Field
          label="Organisation name"
          required
          validationState={errors.organisationName ? 'error' : 'none'}
          validationMessage={errors.organisationName}
        >
          <Input
            value={draft.organisationName}
            onChange={(_event, data) => updateDraft({ organisationName: data.value })}
            maxLength={CUSTOM_DISCLAIMER_FIELD_LIMITS.organisationName}
            placeholder="Enter organisation name..."
            disabled={disabled}
            data-testid="disclaimer-organisation-name-input"
          />
        </Field>
        <Text className={styles.counter} data-testid="disclaimer-organisation-name-counter">
          {draft.organisationName.length}/{CUSTOM_DISCLAIMER_FIELD_LIMITS.organisationName}
        </Text>
      </div>

      <div className={styles.field}>
        <Field
          label="Disclaimer text"
          required
          validationState={errors.text ? 'error' : 'none'}
          validationMessage={errors.text}
        >
          <Textarea
            className={styles.textarea}
            value={draft.text}
            onChange={(_event, data) => updateDraft({ text: data.value })}
            maxLength={CUSTOM_DISCLAIMER_FIELD_LIMITS.text}
            placeholder="Enter disclaimer text..."
            resize="vertical"
            disabled={disabled}
            data-testid="disclaimer-text-input"
          />
        </Field>
        <Text className={styles.counter} data-testid="disclaimer-text-counter">
          {draft.text.length}/{CUSTOM_DISCLAIMER_FIELD_LIMITS.text}
        </Text>
      </div>

      {canGeneratePreview && (
        <div className={styles.previewActions}>
          <Button
            appearance="secondary"
            size="small"
            onClick={() => setPreviewOpen(true)}
            disabled={disabled}
            data-testid="generate-disclaimer-preview-button"
          >
            Generate preview
          </Button>
        </div>
      )}

      <DisclaimerPolicyPicker
        selectedPolicyIds={draft.policyIds}
        onSelectionChange={(policyIds) => updateDraft({ policyIds })}
        currentDisclaimerId={currentDisclaimerId}
        disabled={disabled}
      />

      <DisclaimerPreviewDialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        organisationName={draft.organisationName.trim()}
        text={draft.text}
      />
    </div>
  );
};